import React from 'react';
import { Tag } from 'primereact/tag';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

import LabelName from 'components/UI/LabelName/LabelName';
import ImageComponent from 'shared/ImageComponent';
import constants from 'constants';
import { formatStageName, getProfileImageFilePath, parseReminderData } from 'utils/utils';
import { generateFullName } from 'utils/generateFullNameUtils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { reminderChannels, reminderOptions } from 'constants/dropdownOptions';

const TaskDetailsView = ({ task, timezone, isClientCalendar = false }) => {
  if (!task) return null;

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'info';
    }
  };

  return (
    <form>
      {/* Task Name */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="task_name">
            Task Title
          </LabelName>
        </div>
        <div className="col-md-8 col-12">{task.task_name || 'N/A'}</div>
      </div>

      {/* Priority */}
      {task.priority && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="priority">
              Priority
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <Tag value={task.priority} severity={getPriorityColor(task.priority)} />
          </div>
        </div>
      )}

      {/* Case */}
      {task.case_name && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="case">
              Case
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <div className="mb-2 casename-container text-center F-size13 m-0 p-0">
              <span className="pointer mb-0 p-2 react-tagsinput-tag text-decoration-none">{task.case_name}</span>
            </div>
          </div>
        </div>
      )}

      {/* Practice Area */}
      {task._practice_area_ && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="practice_area">
              Practice Area
            </LabelName>
          </div>
          <div className="col-md-8 col-12">{task._practice_area_}</div>
        </div>
      )}

      {/* Due Date */}
      {task.due_date && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="due_date">
              Due Date
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {convertDateFromApiToUserTimezone(task.due_date, timezone, constants.month_date_year_format)}
          </div>
        </div>
      )}

      {/* Task Stage */}
      {task.task_stage && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="stage">
              Stage
            </LabelName>
          </div>
          <div className="col-md-8 col-12">{formatStageName(task.task_stage)}</div>
        </div>
      )}

      {/* Description */}
      {task.description && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="description">
              Description
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            <div dangerouslySetInnerHTML={{ __html: task.description }} />
          </div>
        </div>
      )}

      {/* Assignees */}
      {task.assign_to && task.assign_to.length > 0 && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="assignees">
              Assigned To
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {task.assign_to.map((assignee, idx) =>
              isClientCalendar ? (
                <div key={idx} className="d-flex align-items-center mb-1">
                  <ImageComponent
                    filePath={assignee?.profile_image || getProfileImageFilePath(assignee?.firm_user_id || assignee?.contact_id)}
                    fileName={generateFullName(assignee)}
                    fileSize="small.jpg"
                    style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                  />
                  <span className="ms-2">
                    {assignee?.firm_user_id && <i className="fas fa-user-tie me-1"></i>}
                    {generateFullName(assignee)}
                  </span>
                </div>
              ) : (
                <Link
                  key={idx}
                  to={assignee?.contact_id ? `/contacts/${assignee?.contact_id}` : `/settings/team-management/${assignee?.firm_user_id}`}
                  className="d-flex align-items-center mb-1 text-decoration-none text-primary-dark"
                >
                  <ImageComponent
                    filePath={assignee?.profile_image || getProfileImageFilePath(assignee?.firm_user_id || assignee?.contact_id)}
                    fileName={generateFullName(assignee)}
                    fileSize="small.jpg"
                    style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                  />
                  <span className="ms-2">
                    {assignee?.firm_user_id && <i className="fas fa-user-tie me-1"></i>}
                    {generateFullName(assignee)}
                  </span>
                </Link>
              )
            )}
          </div>
        </div>
      )}

      {/* Sub-tasks */}
      {task.sub_task && task.sub_task.length > 0 && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="subtasks">
              Sub-tasks
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {task.sub_task.map((subtask, idx) => (
              <div key={idx} className="mb-1">
                <i className="pi pi-check-square me-2"></i>
                {subtask.task_name || subtask.title || subtask.name || 'Subtask'}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reminders */}
      {parseReminderData(task.reminder)?.length > 0 && (
        <div className="d-flex py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="reminder">
              Reminder
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {parseReminderData(task.reminder)?.map((data, index) => {
              return (
                <div
                  className="d-flex input-shadow align-items-center justify-content-center p-2 mb-2 bg-grey mt-2"
                  key={data?.reminder_channel_id || index}
                >
                  <div className="w-100">
                    <div>{reminderChannels?.find((channel) => channel?.value === data?.reminder_channel_type)?.label}</div>
                    <div>
                      {data?.value === 'custom' ? (
                        <>
                          <div>{data?.value}</div>
                          <div>{`${data?.customValue} ${data?.type}`}</div>
                        </>
                      ) : (
                        reminderOptions.find((channel) => channel.value === data?.value)?.label
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Created By */}
      {task.created_by_details && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="created_by">
              Created By
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {isClientCalendar ? (
              <div>{generateFullName(task.created_by_details)}</div>
            ) : (
              <Link
                to={
                  task.created_by_firm_user_id ? `/settings/team-management/${task.created_by_firm_user_id}` : '/settings/team-management'
                }
                className="text-decoration-none text-primary-dark"
              >
                {generateFullName(task.created_by_details)}
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Created At */}
      {task.created_at && (
        <div className="d-flex align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12">
            <LabelName required={false} htmlFor="created_at">
              Created
            </LabelName>
          </div>
          <div className="col-md-8 col-12">
            {convertDateFromApiToUserTimezone(task.created_at, timezone, constants.month_date_year_format)}
          </div>
        </div>
      )}
    </form>
  );
};

export default TaskDetailsView;
